import { useMemo, useCallback } from "react"
import { useOutletContext } from "react-router-dom"
import { useDispatch } from "react-redux"
import { compact, get, isNil, map, omit, pick, includes, some } from "lodash"
import { computeCompositionMetrics } from "modules/compare-composition/selectors/metrics"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { scaledPercentageFormatter, numberFormatter } from "helpers/formatters/numbers"
import { getAllAvailableRollups } from "helpers/rollups"
import { VIEW_TYPES } from "helpers/base"
import { useQueryStringState } from "hooks/query-string"
import { useClassifications } from "resources/classifications"
import { Creators as ProfileCreators } from "model/profileModal"
import PositionTooltip from "components/position-tooltip"
import { createCompositionSingleDateQuery } from "gatherers/single-date/composition"
import { createCompositionMultiDateQuery } from "gatherers/multi-date/composition"
import CompareGraph from "../renderers/compare-graph"
import { useSelectedMetric } from "../hooks/selected-metric"
import { GMV, REFERENCE_EQUITY, POSITIONS, CONCENTRATION, MODEL_COVERAGE, MARKET_CAP, PREDICTED_BETA, HISTORICAL_BETA } from "gatherers/constants"
import CompareTable from "../renderers/compare-table"
import useCompositionTables, { positionSetHasNoGmvOrEquity } from "../hooks/composition-tables"
import DataIntegratedSection from "./data-integrated-section"

// Per-column base overrides mean positionSets[0] isn't reliably representative; treat the shared
// metric/rollup selector as active when any compared column is.
export function isCompositionActive(positionSets) {
  return some(positionSets, "isActive")
}

export default function CompareComposition() {
  const context = useOutletContext()
  const dispatch = useDispatch()

  const compositionTableKey = "composition-table"
  const { modelMetadata, modelId, startDate, endDate, selectedDate, positionSets, selectedMetric: queryStringSelectedMetric, currency } = context
  const [{ selectedGroup: queryStringSelectedGroup }, updateQueryString] = useQueryStringState(compositionTableKey)

  const isActive = isCompositionActive(positionSets)
  const availableMetrics = useMemo(() => computeCompositionMetrics(currency, isActive), [currency, isActive])
  const [classifications, classificationsStatus] = useClassifications()

  const selectedMetric = useSelectedMetric(availableMetrics, queryStringSelectedMetric)
  const { formatter, chartMargins } = selectedMetric
  const currentDate = selectedDate || endDate
  // We disable items that don't have GMV or REFERENCE_EQUITY when looking at those metrics
  const positionSetsMetadata = useMemo(() => map(positionSets, set => ({
    ...set,
    unavailable: set.unavailable || positionSetHasNoGmvOrEquity(set, selectedMetric.id),
  })), [positionSets, selectedMetric.id])

  const availableGroups = useMemo(() => getAllAvailableRollups({ classifications }, currentDate, isActive ? VIEW_TYPES.ACTIVE : VIEW_TYPES.BASE), [classifications, currentDate, isActive])
  const selectedGroup = useSelectedMetric(availableGroups, queryStringSelectedGroup)

  const graph = useMemo(() => ({
    id: "graph",
    name: "Graph",
    renderer: CompareGraph,
    metrics: map(positionSets, set => createCompositionMultiDateQuery(modelId, set.queryPositionSet, set.queryBase, startDate, endDate, {
      type: "metrics",
      defaultZeros: true,
      currency,
      // Metadata uses contextLatestDate; portfolios may not be rolled to that date while period
      // composition queries still return reference equity. Do not strip API values here;
      // useFilteredMetrics hides metrics with no series data.
      hasReferenceEquity: true,
      // We don't add selecteMetric.id here because it would add a dependency and it is not necesseray infromation for createCompositionMultiDateQuery
      noGmvOrEquity: positionSetHasNoGmvOrEquity(set),
    })),
    availableMetrics,
    selectedMetric,
    positionSetsMetadata,
    tickFormatter: formatter,
    chartMargins,
    separator: true,
    formatter,
  }), [positionSets, availableMetrics, selectedMetric, positionSetsMetadata, formatter, chartMargins, modelId, startDate, endDate, currency])

  const tableValueFormatter = useCallback((value) => isNil(value) ? "-" : scaledPercentageFormatter(value), [])
  const labelFormatter = useCallback((value) => value?.label || value?.description || "", [])
  const valueGetter = useCallback((value) => get(value, "economicExposure"), [])
  const positionFormatter = useCallback((position) => {
    let content = <a
      data-test-class="compare-table-link"
      href={`#${position.id}`}
      onClick={() => {
        if (position.assetClass === "index") {
          dispatch(CompositeCreators.openCompositeSecurity(position))
        } else {
          dispatch(ProfileCreators.openSecurityProfile2(position.assetClass, position.id, position.ticker, position.mic))
        }
      }}
    >
      {position.label}
    </a>
    if (position.isUnmodeled) {
      content = <span data-test-class="compare-table-link">{position.label}</span>
    }

    return <PositionTooltip position={omit(position, ["economicExposure", "basePercentEquity", "positionPercentEquity"])}>{content}</PositionTooltip>
  }, [dispatch])

  // Composition needs the model name to display beta tooltips
  const model = useMemo(() => pick(modelMetadata, ["id", "name"]), [modelMetadata])

  const [groupsTable, positionsTable] = useCompositionTables({
    configKey: compositionTableKey,
    model,
    positionSets,
    currentDate,
    currency,
    selectedGroup,
    updateQueryString,
    classificationsStatus,
    labelFormatter,
    positionFormatter,
    valueGetter,
    tableValueFormatter,
    availableGroups,
    classifications,
    renderer: CompareTable,
    gatherer: createCompositionSingleDateQuery,
    rowCount: 10,
  })

  const betaMetrics = useMemo(() =>
    [PREDICTED_BETA, HISTORICAL_BETA].filter(
      metric => !includes(modelMetadata.missingDataFiles, metric),
    ),
  [modelMetadata.missingDataFiles])

  const sections = useMemo(
    () => {
      return compact([
        graph,
        {
          id: "composition",
          name: "Composition",
          separator: true,
          metrics: map(positionSets, set => createCompositionSingleDateQuery(model, set.queryPositionSet, set.queryBase, currentDate, {
            type: "metrics",
            metrics: [GMV, REFERENCE_EQUITY, POSITIONS, CONCENTRATION, MODEL_COVERAGE, MARKET_CAP],
            currency,
            // We don't add selecteMetric.id here because it would add a dependency and it is not necesseray infromation for createCompositionMultiDateQuery
            noGmvOrEquity: positionSetHasNoGmvOrEquity(set),
            hasReferenceEquity: true,
          })),
          defaultExpanded: true,
          rowCount: 6,
        },
        {
          id: "beta",
          name: "Beta",
          alwaysMultipleRows: true,
          separator: true,
          metrics: map(positionSets, set => createCompositionSingleDateQuery(model, set.queryPositionSet, set.queryBase, currentDate, {
            type: "metrics",
            metrics: betaMetrics,
            currency,
            // We don't add selecteMetric.id here because it would add a dependency and it is not necesseray infromation for createCompositionMultiDateQuery
            noGmvOrEquity: positionSetHasNoGmvOrEquity(set),
            hasReferenceEquity: true,
          })),
          defaultExpanded: true,
          rowCount: 2,
        },
        groupsTable,
        positionsTable,
      ])
    },
    // [TODO] how to improve reference integrity of position sets
    [graph, positionSets, groupsTable, positionsTable, model, currentDate, currency, betaMetrics, positionSetsMetadata],
  )

  return sections.map((section, index) => {
    const configKey = `composition-${section.id}`
    return (
      <DataIntegratedSection
        key={configKey}
        configKey={configKey}
        section={section}
        positionSets={positionSets}
        index={index}
        defaultFormatter={numberFormatter}
      />
    )
  })
}
